import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCoins } from "@fortawesome/free-solid-svg-icons";
import config from "./config";
import { APPEARANCE_CHANGE_EVENT, getAppearancePreference, saveAppearancePreference } from "./plansAppearance";
import "./Settings.css";
import "./AccountDeletion.css";

const BILLING_PORTAL = "https://billing.stripe.com/p/login/8wMdUQfLt1PjdpK5kk";
const API_URL = config.API_URL.replace(/\/+$/, "");

function subscriptionStatus(subscription) {
  if (!subscription) return "Pay as you go";
  if (subscription.status === "active") return subscription.cancelAtPeriodEnd ? "Ends after this period" : "Active";
  return ({ canceled: "Canceled", cancelled: "Canceled", past_due: "Payment overdue", unpaid: "Payment required",
    incomplete: "Payment pending", incomplete_expired: "Payment expired", trialing: "Trial", paused: "Paused",
    unsupported: "Needs attention" })[subscription.status] || "Needs attention";
}

function periodDate(value) {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
}

export default function Settings({ refreshUserStateFromStorage }) {
  const [appearance, setAppearance] = useState(getAppearancePreference);
  const [account, setAccount] = useState(null);
  const [accountError, setAccountError] = useState("");
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [userFirstName, setUserFirstName] = useState(() => localStorage.getItem("userFirstName") || "");
  const [userLastName, setUserLastName] = useState(() => localStorage.getItem("userLastName") || "");
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem("loggedInEmail") || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileMessage, setProfileMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const update = () => setAppearance(getAppearancePreference());
    window.addEventListener(APPEARANCE_CHANGE_EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(APPEARANCE_CHANGE_EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    const timeout = setTimeout(() => controller.abort(), 20000);
    setLoading(true);
    setAccountError("");
    setAccount(null);
    fetch(`${API_URL}/api/credits/account`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, signal: controller.signal,
    }).then(async response => {
      if (!response.ok) {
        const error = new Error("Account request failed");
        error.status = response.status;
        throw error;
      }
      return response.json();
    }).then(data => {
      if (!Number.isInteger(data?.purchasedCredits) || !Number.isInteger(data?.availableReports)) throw new Error("Invalid account response");
      if (current) setAccount(data);
    })
      .catch(error => {
        if (current) setAccountError(error.status === 401 ? "Your session has expired. Sign in again to view your account."
          : error.name === "AbortError" ? "Your account took too long to load. Please try again." : "Your account could not be loaded. Please try again.");
      })
      .finally(() => { clearTimeout(timeout); if (current) setLoading(false); });
    return () => { current = false; clearTimeout(timeout); controller.abort(); };
  }, [attempt]);

  const updateUser = async event => {
    event.preventDefault();
    setProfileMessage(null);
    const firstName = userFirstName.trim();
    const lastName = userLastName.trim();
    const email = userEmail.trim();
    if (!firstName || !lastName) {
      setProfileMessage({ error: true, text: "Enter your first and last name." });
      return;
    }
    const formData = new FormData();
    formData.append("userId", localStorage.getItem("userID"));
    formData.append("userFirstName", firstName);
    formData.append("userLastName", lastName);
    formData.append("userEmail", email);
    setSavingProfile(true);
    try {
      const response = await fetch(`${API_URL}/api/User/UpdateUser`, {
        method: "PUT", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, body: formData,
      });
      const result = await response.json();
      if (response.ok && result.status === "success") {
        localStorage.setItem("userFirstName", firstName);
        localStorage.setItem("userLastName", lastName);
        localStorage.setItem("loggedInEmail", email);
        setUserFirstName(firstName); setUserLastName(lastName); setUserEmail(email);
        refreshUserStateFromStorage();
        setProfileMessage({ text: "Profile saved." });
      } else {
        setProfileMessage({ error: true, text: response.status < 500 && result.message ? result.message : "Your profile could not be saved. Please try again." });
      }
    } catch {
      setProfileMessage({ error: true, text: "Your profile could not be saved. Please try again." });
    } finally { setSavingProfile(false); }
  };

  const updatePassword = async event => {
    event.preventDefault();
    setPasswordMessage(null);
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ error: true, text: "Passwords do not match." });
      return;
    }
    if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[~`!@#$%^&*()\-_+={[}\]|:;"'<,>.?/]/.test(newPassword)) {
      setPasswordMessage({ error: true, text: "Use at least 8 characters, including uppercase and lowercase letters, a number, and a symbol." });
      return;
    }
    const formData = new FormData();
    formData.append("newPassword", newPassword);
    setSavingPassword(true);
    try {
      const response = await fetch(`${API_URL}/api/User/UpdatePassword/${localStorage.getItem("userID")}`, {
        method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, body: formData,
      });
      if (response.ok) {
        setNewPassword(""); setConfirmPassword("");
        setPasswordMessage({ text: "Password updated." });
      } else {
        setPasswordMessage({ error: true, text: response.status === 401 ? "Your session has expired. Sign in again." : "Your password could not be updated. Please try again." });
      }
    } catch {
      setPasswordMessage({ error: true, text: "Your password could not be updated. Please try again." });
    } finally { setSavingPassword(false); }
  };

  const subscription = account?.subscription;
  const renewalDate = periodDate(subscription?.renewalDate);
  const monthlyActive = subscription?.status === "active";
  const message = value => value && <p className={`settings-message${value.error ? " settings-message--error" : ""}`} role={value.error ? "alert" : "status"}>{value.text}</p>;

  return <main className={`settings-page settings-page--${appearance}`}>
    <div className="settings-inner">
      <header className="settings-header settings-header--appearance">
        <div className="settings-header-copy">
          <p className="settings-eyebrow">YOUR ACCOUNT</p>
          <h1>Settings</h1>
          <p>Your reports, your details, your preferences.</p>
        </div>
        <div className="settings-appearance-options" role="group" aria-label="Website appearance">
          {["light", "dark"].map(option => <button key={option} type="button" aria-pressed={appearance === option} onClick={() => saveAppearancePreference(option)}>{option === "dark" ? "Dark" : "Light"}</button>)}
        </div>
      </header>

      <section className="settings-card settings-account" aria-labelledby="settings-account-heading" aria-busy={loading}>
        <div className="settings-section-heading">
          <div><h2 id="settings-account-heading">Account overview</h2><p>Everything you need for your next Connectivity Health Report.</p></div>
          <button type="button" className="settings-button settings-button--quiet" disabled={loading} onClick={() => setAttempt(value => value + 1)}>{loading ? "Refreshing..." : "Refresh balance"}</button>
        </div>
        {loading && <p role="status" className="settings-loading">Loading your credits and plan...</p>}
        {accountError && <div className="settings-account-error"><p role="alert">{accountError}</p><button type="button" className="settings-button" onClick={() => setAttempt(value => value + 1)}>Try again</button></div>}
        {account && <>
          <div className="settings-account-grid">
            <div className="settings-credit-summary">
              <p className="settings-metric-label">Credit balance</p>
              <p className="settings-credit-value">{account.purchasedCredits.toLocaleString()} <span>{account.purchasedCredits === 1 ? "credit" : "credits"}</span></p>
              <p>One credit per report. Personal credits never expire.</p>
              <p>{account.teamAllocatedCredits || 0} allocated team credits &middot; {account.teamFundAvailable || 0} general fund credits available</p>
              <div className="settings-actions">
                <Link className="settings-button" to="/team">Manage team credits</Link>
                <Link className="settings-button settings-button--primary settings-add-credits" to="/plans"><FontAwesomeIcon icon={faCoins} aria-hidden="true" />Buy credits</Link>
              </div>
            </div>
            <div className="settings-plan-summary">
              <p className="settings-metric-label">Your plan</p>
              <div className="settings-plan-title"><h3>{subscription?.name || "Pay as you go"}</h3>{subscription && <span className="settings-plan-status">{subscriptionStatus(subscription)}</span>}</div>
              {subscription ? <>
                <p><strong>{subscription.remaining} of {subscription.allowance}</strong> monthly reports available</p>
                {monthlyActive && !subscription.paidPeriod && <p className="settings-plan-note">Your paid report allowance is awaiting confirmation. Refresh your balance shortly.</p>}
                {monthlyActive && renewalDate && <p>{subscription.cancelAtPeriodEnd ? "Plan ends" : "Renews"} {renewalDate}</p>}
                {!monthlyActive && <p>Monthly reports are unavailable. Your credits are still available for reports.</p>}
              </> : <p>Get credits when you need them, or choose a monthly report allowance.</p>}
              <div className="settings-actions">
                <Link className="settings-button" to="/plans">View plans</Link>
                {account.isCustomer && <a className="settings-billing-link" href={BILLING_PORTAL} target="_blank" rel="noopener noreferrer">Manage billing <span className="settings-sr-only">(opens in a new tab)</span><span aria-hidden="true">&#8599;</span></a>}
              </div>
            </div>
          </div>
          <div className="settings-account-footer"><span><strong>{account.availableReports}</strong> reports available in total</span><span>Team credits are used first, then your monthly allowance, then personal credits.</span></div>
        </>}
      </section>

      <div className="settings-details-grid">
        <section className="settings-card" aria-labelledby="settings-profile-heading">
          <h2 id="settings-profile-heading">Profile Information</h2>
          <p>Keep your name and email up to date.</p>
          <form className="settings-form" onSubmit={updateUser}>
            <fieldset disabled={savingProfile}>
              <div className="settings-name-fields">
                <div><label htmlFor="settings-first-name">First name</label><input id="settings-first-name" autoComplete="given-name" maxLength={255} required value={userFirstName} onChange={event => setUserFirstName(event.target.value)} /></div>
                <div><label htmlFor="settings-last-name">Last name</label><input id="settings-last-name" autoComplete="family-name" maxLength={255} required value={userLastName} onChange={event => setUserLastName(event.target.value)} /></div>
              </div>
              <label htmlFor="settings-email">Email address</label><input id="settings-email" type="email" autoComplete="email" required value={userEmail} onChange={event => setUserEmail(event.target.value)} />
              <button className="settings-button settings-button--primary" type="submit">{savingProfile ? "Saving..." : "Save profile"}</button>
            </fieldset>
            {message(profileMessage)}
          </form>
        </section>
        <section className="settings-card" aria-labelledby="settings-password-heading">
          <h2 id="settings-password-heading">Change password</h2>
          <p>Choose a strong password to protect your account.</p>
          <form className="settings-form" onSubmit={updatePassword}>
            <fieldset disabled={savingPassword}>
              <label htmlFor="settings-new-password">New password</label><input id="settings-new-password" type="password" autoComplete="new-password" required aria-describedby="settings-password-help" value={newPassword} onChange={event => setNewPassword(event.target.value)} />
              <label htmlFor="settings-confirm-password">Confirm new password</label><input id="settings-confirm-password" type="password" autoComplete="new-password" required value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} />
              <p id="settings-password-help" className="settings-field-help">At least 8 characters with uppercase and lowercase letters, a number, and a symbol.</p>
              <button className="settings-button" type="submit">{savingPassword ? "Updating..." : "Update password"}</button>
            </fieldset>
            {message(passwordMessage)}
          </form>
        </section>
      </div>
      <section className="settings-card settings-delete-account" aria-labelledby="settings-delete-account-heading">
        <h2 id="settings-delete-account-heading">Delete account</h2>
        <p>Permanently remove your account. You will review the details and confirm on the next page.</p>
        <Link className="settings-button" to="/account/delete">Delete my account</Link>
      </section>
    </div>
  </main>;
}
